import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { MaintenanceRecord } from '../utils/types';
import { useSettings } from '../contexts/SettingsContext';
import { getCustomerById } from '../services/customerService';

interface MaintenanceBarcodeLabelProps {
  maintenance: MaintenanceRecord;
  labelSize: string;
}

const MaintenanceBarcodeLabel: React.FC<MaintenanceBarcodeLabelProps> = ({ maintenance, labelSize }) => {
  const { settings } = useSettings();
  const [customerPhone, setCustomerPhone] = useState<string>('غير متوفر');



  // تحميل رقم هاتف العميل
  useEffect(() => {
    const loadCustomerPhone = async () => {
      if (maintenance.customerId) {
        try {
          const customer = await getCustomerById(maintenance.customerId);
          if (customer && customer.phone) {
            setCustomerPhone(customer.phone);
          } else {
            setCustomerPhone('غير متوفر');
          }
        } catch (error) {
          console.error('Error loading customer phone:', error);
          setCustomerPhone('غير متوفر');
        }
      } else {
        setCustomerPhone('غير متوفر');
      }
    };

    loadCustomerPhone();
  }, [maintenance.customerId]);

  // Ya no generamos código de barras

  return (
    <Paper
      sx={{
        // En la vista previa, adaptamos al tamaño seleccionado
        width: labelSize === 'tiny' ? '50mm' : '58mm',
        padding: labelSize === 'tiny' ? '1.5mm' : '5mm',
        margin: '0 auto',
        backgroundColor: '#fff',
        height: labelSize === 'tiny' ? '25mm' : labelSize === 'small' ? '40mm' : labelSize === 'medium' ? '60mm' : '80mm',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: labelSize === 'tiny' ? 'space-between' : 'center',
        alignItems: 'center',
        textAlign: 'center',
        overflow: 'hidden',
        // En la impresión, se aplicará la rotación desde el componente PrintBarcodeDialog
        '@media print': {
          boxShadow: 'none',
          margin: 0,
          padding: labelSize === 'tiny' ? '1.5mm' : '2mm'
        }
      }}
    >
      {/* Company Name/Logo */}
      <Box
        textAlign="center"
        className="company-header"
        width="100%"
        sx={{
          mb: labelSize === 'tiny' ? 0 : 1,
          flex: labelSize === 'tiny' ? '0 0 auto' : 'none'
        }}
      >
        <Typography variant="h6" sx={{
          fontSize: labelSize === 'tiny' ? '12px' : labelSize === 'small' ? '16px' : '18px',
          fontWeight: 'bold',
          fontFamily: "'Cairo', sans-serif",
          lineHeight: labelSize === 'tiny' ? 1.1 : 1.2,
          margin: 0
        }} component="h1">
          {settings?.companyName || 'مركز صيانة التقنية'}
        </Typography>
        <Typography variant="body2" sx={{
          fontSize: labelSize === 'tiny' ? '9px' : labelSize === 'small' ? '12px' : '14px',
          fontFamily: "'Cairo', sans-serif",
          lineHeight: labelSize === 'tiny' ? 1.1 : 1.2,
          margin: 0
        }} component="p">
          {settings?.companyPhone || ''}
        </Typography>
      </Box>

      {/* Customer Info */}
      <Box
        className="customer-info"
        width="100%"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: labelSize === 'tiny' ? '0.5mm' : '1mm',
          flex: labelSize === 'tiny' ? '1 1 auto' : 'none',
          justifyContent: labelSize === 'tiny' ? 'space-evenly' : 'flex-start'
        }}
      >
        <Typography variant="body2" sx={{
          fontSize: labelSize === 'tiny' ? '11px' : labelSize === 'small' ? '15px' : '17px',
          fontWeight: 'bold',
          fontFamily: "'Cairo', sans-serif",
          width: '100%',
          lineHeight: labelSize === 'tiny' ? 1.1 : 1.2,
          margin: 0
        }} component="p" className="customer-name">
          العميل: {maintenance?.customerName || 'غير محدد'}
        </Typography>

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}>
          <Typography variant="body2" sx={{
            fontSize: labelSize === 'tiny' ? '10px' : labelSize === 'small' ? '14px' : '16px',
            fontFamily: "'Cairo', sans-serif",
            lineHeight: labelSize === 'tiny' ? 1.1 : 1.2,
            margin: 0,
            flex: 1
          }} component="p">
            {customerPhone}
          </Typography>
          <Typography variant="body2" sx={{
            fontSize: labelSize === 'tiny' ? '12px' : labelSize === 'small' ? '16px' : '18px',
            fontFamily: "'Cairo', sans-serif",
            lineHeight: labelSize === 'tiny' ? 1.1 : 1.2,
            margin: 0,
            fontWeight: 'bold',
            marginLeft: '2mm'
          }} component="p">
            {maintenance?.orderNumber || (maintenance?.id && !isNaN(parseInt(maintenance.id)) ? parseInt(maintenance.id) : '1')}
            {/* حجم الشاشة */}
            {maintenance?.devices?.[0]?.size ? ` - ${maintenance.devices[0].size}"` : ''}
          </Typography>
        </Box>
        {/* ماركة الجهاز تحت رقم الهاتف */}
        {maintenance?.devices?.[0]?.brand && (
          <Typography variant="body2" sx={{
            fontSize: labelSize === 'tiny' ? '10px' : labelSize === 'small' ? '14px' : '16px',
            fontFamily: "'Cairo', sans-serif",
            width: '100%',
            lineHeight: 1.2,
            margin: 0
          }} component="p">
            {maintenance.devices[0].brand}
          </Typography>
        )}
        <Typography variant="body2" sx={{
          fontSize: labelSize === 'tiny' ? '10px' : labelSize === 'small' ? '14px' : '16px',
          fontFamily: "'Cairo', sans-serif",
          width: '100%',
          fontWeight: 'bold',
          lineHeight: labelSize === 'tiny' ? 1.1 : 1.2,
          margin: 0
        }} component="p">
          المشكلة: {maintenance?.issue?.substring(0, labelSize === 'tiny' ? 12 : 30)}{maintenance?.issue?.length > (labelSize === 'tiny' ? 12 : 30) ? '...' : ''}
        </Typography>
        {labelSize !== 'tiny' && (
          <Typography variant="body2" sx={{
            fontSize: labelSize === 'small' ? '14px' : '16px',
            fontFamily: "'Cairo', sans-serif",
            width: '100%',
            lineHeight: 1.2,
            margin: 0
          }} component="p">
            تاريخ الاستلام: {maintenance?.receivedAt ? new Date(maintenance.receivedAt).toLocaleDateString('ar-EG') : ''}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default MaintenanceBarcodeLabel;
